/**
 * Bounding box para área de proyecto
 */
export interface BoundingBox {
  minLng: number;
  minLat: number;
  maxLng: number;
  maxLat: number;
}

/**
 * Configuración de asociación punto-red por proyecto
 */
export interface AssociationConfig {
  /** Distancia máxima para asociar a un nodo (metros). Default: 6 */
  nodeThresholdMeters: number;
  /** Distancia máxima para asociar a un edge (metros). Default: 15 */
  edgeThresholdMeters: number;
  /** Si true, asocia automáticamente al crear entidades punto. Default: true */
  autoAssociateOnCreate?: boolean;
  /** Si true, recalcula cuando la red cambia. Default: true */
  autoRecalculateOnNetworkChange?: boolean;
}

/**
 * Configuración por defecto de asociación
 */
export const DEFAULT_ASSOCIATION_CONFIG: AssociationConfig = {
  nodeThresholdMeters: 6,
  edgeThresholdMeters: 15,
  autoAssociateOnCreate: true,
  autoRecalculateOnNetworkChange: true,
};

/**
 * Tenant (información básica)
 */
export interface ProjectTenant {
  id: string;
  name: string;
  slug: string;
}

/**
 * Módulos disponibles en WESTEER
 */
export type WesteerModule =
  | 'transit'
  | 'transport'
  | 'roadways'
  | 'environmental'
  | 'infrastructure'
  | 'urban'
  | 'social'
  | 'services'
  | 'safety'
  | 'waste'
  | 'urban-network';

/**
 * Usuario básico (para relaciones)
 */
export interface ProjectUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
}

/**
 * Relación usuario–proyecto tal como la devuelve el backend (modo edición)
 */
export interface ProjectUserRelation {
  user: ProjectUser;
  userId?: string;
  role: 'viewer' | 'editor' | 'admin';
}

/**
 * Proyecto
 */
export interface Project {
  id: string;
  name: string;
  description: string | null;
  bounds: BoundingBox;
  /**
   * Límites personalizados del proyecto (opcional)
   * @remarks
   * Polígono o MultiPolígono GeoJSON que define el área exacta del proyecto.
   * Si está definido, se usa en vez del bounds rectangular.
   * Casos de uso: límites administrativos, áreas dibujadas manualmente.
   */
  customBounds?: any | null;
  /**
   * Tipo de proyecto
   * - 'standard': proyecto de análisis territorial normal
   * - 'territorial_master': PTM — base territorial reutilizable por verticales
   * @default 'standard'
   */
  projectType?: 'standard' | 'territorial_master';
  /**
   * Número del cliente (se copia del tenant.clientNumber)
   * Forma la primera parte del N° OT: CCCC-PP
   * @example 1 (se muestra como 0001)
   */
  clientNumber?: number | null;
  /**
   * Número secuencial del proyecto dentro del cliente (1, 2, 3, ..., 99)
   * Se asigna automáticamente al crear el proyecto.
   * Forma la segunda parte del N° OT: CCCC-PP
   * @example 1 (se muestra como 01)
   */
  projectNumber?: number | null;
  /**
   * Fecha de inicio del período de vigencia del servicio
   */
  startDate?: string | null;
  /**
   * Fecha de fin del período de vigencia del servicio
   */
  endDate?: string | null;
  /**
   * Módulos activos/contratados para este proyecto
   */
  activeModules?: WesteerModule[];
  /**
   * Resolución de la malla canónica H3 del proyecto.
   * Solo configurable durante la creación; no puede modificarse después.
   */
  canonicalGridResolution?: number;
  /**
   * Configuración de asociación punto-red
   * @remarks
   * Define los umbrales y comportamiento para asociar entidades punto
   * (paradas, luminarias, etc.) a la red canónica (edges/nodes).
   */
  associationConfig?: AssociationConfig | null;
  /**
   * Usuarios con acceso al proyecto (incluye rol por proyecto)
   */
  projectUsers?: ProjectUserRelation[];
  /**
   * Información del tenant al que pertenece el proyecto
   */
  tenant?: ProjectTenant;
  /**
   * Administrador/Responsable del proyecto
   */
  projectAdmin?: ProjectUser | null;
  /**
   * Usuario que creó el proyecto (auditoría)
   */
  createdBy?: ProjectUser;
  createdAt: string;
  updatedAt: string;
}

/**
 * Escenario
 */
export interface Scenario {
  id: string;
  projectId: string;
  name: string;
  type: 'baseline' | 'proposed' | 'sensitivity';
  status: 'draft' | 'published' | 'archived';
  version: number;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Tipo de scope territorial para proyectos de base nacional.
 */
export type NationalScopeType = 'country' | 'admin_single' | 'admin_multi' | 'custom_bbox';

/**
 * Resultado del chequeo de cobertura de la Red Urbana Nacional.
 */
export interface NationalCoverageResult {
  available: boolean;
  edgeCount: number;
  nodeCount: number;
  territorialCoverageStatus: 'ready' | 'no_coverage';
  resolvedBoundaries?: Array<{ adminCode: string; adminName: string; adminLevel: number }>;
}

/**
 * DTO para crear proyecto (espejo del CreateProjectDto del backend)
 */
export interface CreateProjectDto {
  name: string;
  description: string;
  bounds?: BoundingBox;
  /**
   * Límites personalizados del proyecto (opcional)
   * Polígono o MultiPolígono GeoJSON que define el área exacta del proyecto.
   */
  customBounds?: any;
  startDate?: string;
  endDate?: string;
  activeModules?: WesteerModule[];
  projectAdminId?: string;
  projectUsers?: Array<{ userId: string; role: 'viewer' | 'editor' | 'admin' }>;
  /** Resolución H3 de la malla canónica (7–11, default 9). Solo en creación. */
  canonicalGridResolution?: number;
  /** Configuración de asociación punto-red */
  associationConfig?: AssociationConfig;
  /** Tipo de proyecto: 'standard' | 'territorial_master' | 'national'. Default: 'standard' */
  projectType?: 'standard' | 'territorial_master' | 'national';
  // ── Nacional (usesNationalBase=true) ────────────────────────────────────────
  usesNationalBase?: boolean;
  countryCode?: string;
  /** Tipo de scope territorial para proyectos nacionales */
  scopeType?: NationalScopeType;
  /** Código INDEC del boundary (admin_single) */
  scopeBoundaryCode?: string;
  /** Códigos INDEC de boundaries (admin_multi, mínimo 2) */
  scopeBoundaryCodes?: string[];
  /** Nivel admin: 1=provincia, 2=departamento */
  scopeAdminLevel?: 1 | 2;
}

/**
 * DTO para crear escenario
 */
export interface CreateScenarioDto {
  name: string;
  type: 'baseline' | 'proposed' | 'sensitivity';
}

/**
 * Formatea el número de orden de trabajo (Work Order Number)
 * Formato: CCCC-PP
 * - CCCC: Número de cliente (0001-9999)
 * - PP: Número de proyecto del cliente (01-99)
 *
 * @example
 * formatWorkOrderNumber(1, 1) // "0001-01"
 * formatWorkOrderNumber(15, 2) // "0015-02"
 * formatWorkOrderNumber(9999, 99) // "9999-99"
 */
export function formatWorkOrderNumber(clientNumber: number, projectNumber: number): string {
  const clientStr = clientNumber.toString().padStart(4, '0');
  const projectStr = projectNumber.toString().padStart(2, '0');
  return `${clientStr}-${projectStr}`;
}

// ==================== EXTENDED PROJECT TYPES ====================
// Para el panel expandible del SuperAdmin Dashboard

/**
 * Estado de una fase del pipeline
 */
export interface PhaseStatus {
  status: 'pending' | 'queued' | 'running' | 'completed' | 'warning' | 'failed';
  completedAt: string | null;
  durationMs: number | null;
  details: string | null;
}

/**
 * Estado completo del pipeline
 */
export interface PipelineStatus {
  overallStatus: 'pending' | 'building' | 'ready' | 'warning' | 'error';
  progressPercent: number;
  phases: {
    bounds: PhaseStatus;
    h3Grids: PhaseStatus;
    baseNetwork: PhaseStatus;
    urbanNetwork: PhaseStatus;
    syntax: PhaseStatus;
  };
  lastError: {
    phase: string;
    message: string;
    occurredAt: string;
  } | null;
}

/**
 * Estadísticas de la red urbana
 */
export interface NetworkStats {
  nodesCount: number;
  edgesCount: number;
  totalLengthKm: number;
  version: number;
  lastUpdatedAt: string | null;
}

/**
 * Estado de sintaxis espacial
 */
export interface SyntaxStats {
  status: 'pending' | 'calculating' | 'valid' | 'stale' | 'error';
  version: number;
  calculatedAt: string | null;
  isStale: boolean;
  coverage: number;
}

/**
 * Datos de un módulo
 */
export interface ModuleData {
  moduleId: string;
  moduleName: string;
  isContracted: boolean;
  hasData: boolean;
  entitiesCount: number;
  lastUpdatedAt: string | null;
  entities: {
    type: string;
    typeName: string;
    count: number;
  }[];
}

/**
 * Estado de uso del cliente
 */
export interface UsageStats {
  status: 'active' | 'low' | 'inactive' | 'abandoned';
  lastAccessedAt: string | null;
  usersAssigned: number;
  usersActive7d: number;
}

/**
 * Información del tenant simplificada
 */
export interface TenantSummary {
  id: string;
  name: string;
  slug: string;
  plan: 'basic' | 'pro' | 'enterprise';
  status: 'active' | 'trial' | 'suspended' | 'cancelled';
}

/**
 * Información del usuario simplificada
 */
export interface UserSummary {
  id: string;
  name: string;
  email: string;
}

/**
 * Proyecto con información extendida para SuperAdmin
 */
export interface ProjectExtended {
  // Datos básicos
  id: string;
  name: string;
  description: string | null;
  clientNumber: number | null;
  projectNumber: number | null;
  createdAt: string;
  updatedAt: string;

  // Tenant
  tenant: TenantSummary;

  // Admin y usuarios
  projectAdmin: UserSummary | null;
  usersCount: number;

  // Pipeline
  pipeline: PipelineStatus;

  // Red urbana
  network: NetworkStats;

  // Sintaxis
  syntax: SyntaxStats;

  // Módulos
  modules: ModuleData[];
  modulesContracted: number;
  modulesWithData: number;

  // Uso
  usage: UsageStats;

  // Fechas de vigencia
  startDate: string | null;
  endDate: string | null;
  daysRemaining: number | null;
}

/**
 * Resumen estadístico de todos los proyectos
 */
export interface ProjectsSummary {
  total: number;
  byPipelineStatus: {
    ready: number;
    building: number;
    error: number;
    pending: number;
    warning: number;
  };
  byUsage: {
    active: number;
    low: number;
    inactive: number;
    abandoned: number;
  };
}

/**
 * Respuesta del endpoint /admin/projects/extended
 */
export interface ProjectsExtendedResponse {
  projects: ProjectExtended[];
  summary: ProjectsSummary;
}
